// =============================================================================
// MeuDriver — Analytics do Admin (H2)
// =============================================================================

import { useState } from 'react'
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import {
  TrendingUp,
  BookOpen,
  CheckCircle,
  Users,
  DollarSign,
  Star,
  ChevronDown,
  BarChart2,
  Percent,
  Timer,
} from 'lucide-react'
import { format, parseISO } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import {
  useRevenueMetrics,
  useDailyBookings,
  useTopInstructors,
  useSubscriptionMetrics,
  useRevenueAnalytics,
  useTopInstructorsAnalytics,
  useConversionAnalytics,
  type RevenueAnalyticsRow,
  type TopInstructorAnalyticsRow,
} from '@/hooks/useAdminAnalytics'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { formatCurrency, getInitials } from '@/lib/utils'

// ─── Tipos ────────────────────────────────────────────────────────────────────

type Period = 7 | 30 | 90

// ─── Period Selector ─────────────────────────────────────────────────────────

interface PeriodSelectorProps {
  value: Period
  onChange: (p: Period) => void
}

function PeriodSelector({ value, onChange }: PeriodSelectorProps) {
  const options: Period[] = [7, 30, 90]
  return (
    <div className="flex items-center gap-1 rounded-lg border border-slate-200 dark:border-gray-700 p-0.5 bg-slate-50 dark:bg-gray-900">
      {options.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onChange(p)}
          className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-all ${
            value === p
              ? 'bg-white dark:bg-gray-800 text-primary shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          {p}d
        </button>
      ))}
    </div>
  )
}

// ─── KPI Card ────────────────────────────────────────────────────────────────

interface KpiCardProps {
  title: string
  value: string
  sub?: string
  icon: React.ElementType
  color: string
  loading?: boolean
}

function KpiCard({ title, value, sub, icon: Icon, color, loading }: KpiCardProps) {
  return (
    <Card className="dark:bg-gray-900">
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{title}</p>
            {loading ? (
              <Skeleton className="h-7 w-24" />
            ) : (
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</p>
            )}
            {sub && !loading && (
              <p className="text-xs text-muted-foreground">{sub}</p>
            )}
          </div>
          <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${color}`}>
            <Icon className="h-5 w-5 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ─── Tooltip customizado ──────────────────────────────────────────────────────

interface ChartTooltipProps {
  active?: boolean
  payload?: Array<{ name: string; value: number; color: string }>
  label?: string
}

function ChartTooltip({ active, payload, label }: ChartTooltipProps) {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg p-3 text-xs space-y-1">
      <p className="font-semibold text-gray-700 dark:text-gray-300 mb-1">{label}</p>
      {payload.map((p) => (
        <div key={p.name} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full inline-block" style={{ background: p.color }} />
          <span className="text-muted-foreground">{p.name}:</span>
          <span className="font-semibold">{typeof p.value === 'number' && p.name === 'Receita' ? formatCurrency(p.value) : p.value}</span>
        </div>
      ))}
    </div>
  )
}

// ─── AdminAnalytics ───────────────────────────────────────────────────────────

export default function AdminAnalytics() {
  const [period, setPeriod] = useState<Period>(30)

  const { data: summary, isLoading: loadingSummary } = useRevenueMetrics(period)
  const { data: daily = [], isLoading: loadingDaily } = useDailyBookings(period)
  const { data: topInstructors = [], isLoading: loadingTop } = useTopInstructors(10)
  const { data: subs, isLoading: loadingSubs } = useSubscriptionMetrics()

  // Q3 — dados avançados
  const { data: revenueByMonth = [], isLoading: loadingRevMonth } = useRevenueAnalytics(12)
  const { data: topInstructorsQ3 = [], isLoading: loadingTopQ3 } = useTopInstructorsAnalytics(10)
  const { data: conversion, isLoading: loadingConversion } = useConversionAnalytics()

  // Formata dados para os gráficos
  const chartData = daily.map((d) => ({
    date: format(parseISO(d.booking_date), 'dd/MM', { locale: ptBR }),
    Receita: d.revenue,
    Concluídos: d.completed,
    Cancelados: d.cancelled,
    Total: d.total,
  }))

  const completionRate =
    summary && summary.total_bookings > 0
      ? Math.round((summary.completed_bookings / summary.total_bookings) * 100)
      : 0

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Analytics</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Visão consolidada do negócio
          </p>
        </div>
        <PeriodSelector value={period} onChange={setPeriod} />
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          title={`Receita (${period}d)`}
          value={summary ? formatCurrency(summary.total_revenue) : '—'}
          sub={`Ticket médio ${summary ? formatCurrency(summary.avg_booking_value) : '—'}`}
          icon={DollarSign}
          color="bg-emerald-500"
          loading={loadingSummary}
        />
        <KpiCard
          title={`Bookings (${period}d)`}
          value={summary ? String(summary.total_bookings) : '—'}
          sub={`${summary?.cancelled_bookings ?? 0} cancelamentos`}
          icon={BookOpen}
          color="bg-brand-600"
          loading={loadingSummary}
        />
        <KpiCard
          title="Taxa de conclusão"
          value={loadingSummary ? '—' : `${completionRate}%`}
          sub={`${summary?.completed_bookings ?? 0} aulas concluídas`}
          icon={CheckCircle}
          color="bg-teal-500"
          loading={loadingSummary}
        />
        <KpiCard
          title={`Novos alunos (${period}d)`}
          value={summary ? String(summary.new_students) : '—'}
          sub={`${summary?.new_instructors ?? 0} novos instrutores`}
          icon={Users}
          color="bg-orange-500"
          loading={loadingSummary}
        />
      </div>

      {/* MRR + Subscriptions */}
      {subs && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KpiCard
            title="MRR estimado"
            value={formatCurrency(subs.mrr)}
            sub={`${subs.totalActive} assinaturas ativas`}
            icon={TrendingUp}
            color="bg-purple-500"
            loading={loadingSubs}
          />
          <KpiCard
            title="Churn (30d)"
            value={`${subs.churnRate}%`}
            sub={`${subs.cancelledLast30} cancelamentos`}
            icon={ChevronDown}
            color="bg-red-500"
            loading={loadingSubs}
          />
          <KpiCard
            title="Alunos Premium"
            value={String(subs.activeStudentSubs)}
            icon={Star}
            color="bg-yellow-500"
            loading={loadingSubs}
          />
          <KpiCard
            title="Instrutores Pro"
            value={String(subs.activeInstructorSubs)}
            icon={Star}
            color="bg-orange-400"
            loading={loadingSubs}
          />
        </div>
      )}

      {/* Gráfico de linha: receita diária */}
      <Card className="dark:bg-gray-900">
        <CardHeader>
          <CardTitle className="text-base">Receita diária (R$)</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingDaily ? (
            <Skeleton className="h-64 w-full rounded-xl" />
          ) : chartData.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-16">
              Sem dados no período selecionado.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v: number) => `R$${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}`}
                />
                <Tooltip content={<ChartTooltip />} />
                <Line
                  type="monotone"
                  dataKey="Receita"
                  stroke="#0054b3"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Gráfico de barras: bookings por status */}
      <Card className="dark:bg-gray-900">
        <CardHeader>
          <CardTitle className="text-base">Bookings por status</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingDaily ? (
            <Skeleton className="h-64 w-full rounded-xl" />
          ) : chartData.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-16">
              Sem dados no período selecionado.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={chartData} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Concluídos" fill="#14b8a6" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Cancelados" fill="#f97316" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Total" fill="#0d6ee8" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Tabela top instrutores */}
      <Card className="dark:bg-gray-900">
        <CardHeader>
          <CardTitle className="text-base">Top 10 instrutores por receita</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loadingTop ? (
            <div className="space-y-3 p-6">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full rounded-lg" />
              ))}
            </div>
          ) : topInstructors.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">
              Nenhum dado disponível.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b dark:border-gray-800">
                    <th className="text-left px-6 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Instrutor
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Aulas
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Concluídas
                    </th>
                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Rating
                    </th>
                    <th className="text-right px-6 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Receita
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {topInstructors.map((inst, idx) => (
                    <tr
                      key={inst.instructor_id}
                      className="hover:bg-slate-50 dark:hover:bg-gray-800/50 transition-colors"
                    >
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-3">
                          <span className="w-5 text-xs font-bold text-muted-foreground/50">
                            {idx + 1}
                          </span>
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={inst.avatar_url ?? undefined} />
                            <AvatarFallback className="text-xs bg-brand-100 text-brand-700">
                              {getInitials(inst.name ?? 'IN')}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium text-gray-900 dark:text-gray-100 truncate max-w-[140px]">
                            {inst.name ?? '—'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-muted-foreground">
                        {inst.total_bookings}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Badge
                          variant="outline"
                          className="text-xs font-semibold border-teal-200 text-teal-700 dark:border-teal-800 dark:text-teal-400"
                        >
                          {inst.completed_bookings}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {inst.avg_rating != null ? (
                          <span className="flex items-center justify-end gap-1 text-yellow-600">
                            <Star className="h-3 w-3 fill-current" />
                            {inst.avg_rating.toFixed(1)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-right font-semibold text-emerald-700 dark:text-emerald-400">
                        {formatCurrency(inst.total_revenue)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Q3: Seção Analytics Avançado ──────────────────────────────────── */}

      <div className="pt-4 border-t dark:border-gray-800">
        <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-1 flex items-center gap-2">
          <BarChart2 className="h-5 w-5 text-brand-600" />
          Analytics avançado
        </h2>
        <p className="text-sm text-muted-foreground mb-4">
          Receita mensal, crescimento e conversão de novos usuários.
        </p>
      </div>

      {/* Cards de conversão */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          title="Cadastros (total)"
          value={conversion ? String(conversion.total_registrations) : '—'}
          icon={Users}
          color="bg-brand-600"
          loading={loadingConversion}
        />
        <KpiCard
          title="Convertidos"
          value={conversion ? String(conversion.total_first_bookings) : '—'}
          sub="fizeram ao menos 1 aula"
          icon={CheckCircle}
          color="bg-teal-500"
          loading={loadingConversion}
        />
        <KpiCard
          title="Taxa de conversão"
          value={conversion ? `${conversion.conversion_rate}%` : '—'}
          icon={Percent}
          color="bg-emerald-600"
          loading={loadingConversion}
        />
        <KpiCard
          title="Dias até 1ª aula"
          value={conversion ? `${conversion.avg_days_to_first_booking}d` : '—'}
          sub="média após cadastro"
          icon={Timer}
          color="bg-orange-500"
          loading={loadingConversion}
        />
      </div>

      {/* Gráfico de linha dupla: GMV e comissão por mês */}
      <Card className="dark:bg-gray-900">
        <CardHeader>
          <CardTitle className="text-base">GMV e comissão por mês (12 meses)</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingRevMonth ? (
            <Skeleton className="h-64 w-full rounded-xl" />
          ) : revenueByMonth.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-16">
              Sem dados disponíveis.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <LineChart
                data={revenueByMonth.map((r: RevenueAnalyticsRow) => ({
                  mes: r.month_label.slice(0, 7),
                  GMV: r.gmv,
                  Comissão: r.commission,
                }))}
                margin={{ top: 4, right: 8, bottom: 0, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis
                  tick={{ fontSize: 11 }}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v: number) => `R$${v >= 1000 ? `${(v / 1000).toFixed(1)}k` : v}`}
                />
                <Tooltip
                  formatter={(value: number) => formatCurrency(value)}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="GMV"      stroke="#0054b3" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
                <Line type="monotone" dataKey="Comissão" stroke="#14b8a6" strokeWidth={2} dot={false} activeDot={{ r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Gráfico de barras: novos cadastros por mês */}
      <Card className="dark:bg-gray-900">
        <CardHeader>
          <CardTitle className="text-base">Novos cadastros por mês (alunos vs instrutores)</CardTitle>
        </CardHeader>
        <CardContent>
          {loadingRevMonth ? (
            <Skeleton className="h-64 w-full rounded-xl" />
          ) : revenueByMonth.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-16">
              Sem dados disponíveis.
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={revenueByMonth.map((r: RevenueAnalyticsRow) => ({
                  mes: r.month_label.slice(0, 7),
                  Alunos: r.new_students,
                  Instrutores: r.new_instructors,
                }))}
                margin={{ top: 4, right: 8, bottom: 0, left: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                <XAxis dataKey="mes" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Alunos"      fill="#0054b3" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Instrutores" fill="#14b8a6" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Tabela top instrutores Q3 */}
      <Card className="dark:bg-gray-900">
        <CardHeader>
          <CardTitle className="text-base">Top 10 instrutores por GMV (Q3)</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loadingTopQ3 ? (
            <div className="space-y-3 p-6">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full rounded-lg" />
              ))}
            </div>
          ) : topInstructorsQ3.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">
              Nenhum dado disponível.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b dark:border-gray-800">
                    <th className="text-left px-6 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Instrutor</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Aulas</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Rating</th>
                    <th className="text-right px-6 py-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">GMV</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                  {topInstructorsQ3.map((inst: TopInstructorAnalyticsRow, idx) => (
                    <tr key={inst.instructor_id} className="hover:bg-slate-50 dark:hover:bg-gray-800/50 transition-colors">
                      <td className="px-6 py-3">
                        <div className="flex items-center gap-3">
                          <span className="w-5 text-xs font-bold text-muted-foreground/50">{idx + 1}</span>
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={inst.avatar_url ?? undefined} />
                            <AvatarFallback className="text-xs bg-brand-100 text-brand-700">
                              {getInitials(inst.instructor_name ?? 'IN')}
                            </AvatarFallback>
                          </Avatar>
                          <span className="font-medium text-gray-900 dark:text-gray-100 truncate max-w-[140px]">
                            {inst.instructor_name ?? '—'}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right text-muted-foreground">{inst.booking_count}</td>
                      <td className="px-4 py-3 text-right">
                        {inst.avg_rating != null ? (
                          <span className="flex items-center justify-end gap-1 text-yellow-600">
                            <Star className="h-3 w-3 fill-current" />
                            {Number(inst.avg_rating).toFixed(1)}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-right font-semibold text-emerald-700 dark:text-emerald-400">
                        {formatCurrency(inst.gmv)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
